import { motion } from "framer-motion";
import factoryImg from "../../assets/aluminum_extrusion_factory_hero_1774255270708.png";
import hardwareImg from "../../assets/aluminum_hardware_materials_1774255292445.png";
import { Link } from "react-router-dom";
import LazyImage from "../../common/LazyImage";
import { useSiteSettings } from "../../context/SiteSettingsContext";


export function ExtrusionFactory() {
  return (
    <section id="factory" className="py-20 bg-transparent overflow-hidden">
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <p className="text-secondary font-bold tracking-[.3em] uppercase text-xs">
              State-of-the-Art Factory
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
              Mastering the <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500">
                Aluminium Extrusion
              </span>
            </h2>
            <div className="w-20 h-1 bg-white" />

            <p className="text-white/70 text-lg leading-relaxed">
              Our Surat-based extrusion facility is engineered for high-precision manufacturing. With advanced press technology and automated handling systems, we transform 6xxx series aluminium billets into complex geometric profiles with exceptional dimensional accuracy.
            </p>

            <div className="grid grid-cols-2 gap-8 pt-6">
              <div>
                <h4 className="text-white font-bold text-2xl">1000+ Tons</h4>
                <p className="text-white/50 text-sm uppercase tracking-wider">Monthly Capacity</p>
              </div>
              <div>
                <h4 className="text-white font-bold text-2xl">Precision</h4>
                <p className="text-white/50 text-sm uppercase tracking-wider">+/- 0.1mm Tolerance</p>
              </div>
              <div>
                <h4 className="text-white font-bold text-2xl">In-house</h4>
                <p className="text-white/50 text-sm uppercase tracking-wider">Die Design Studio</p>
              </div>
              <div>
                <h4 className="text-white font-bold text-2xl">Testing</h4>
                <p className="text-white/50 text-sm uppercase tracking-wider">Metallurgical Lab</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative group"
          >
            <div className="absolute -inset-4 bg-white/5 rounded-2xl blur-2xl group-hover:bg-white/10 transition-all" />
            <LazyImage
              src={factoryImg}
              alt="Extrusion Factory"
              className="relative w-full h-[500px] object-cover rounded-2xl shadow-2xl transition-all duration-700"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export function MaterialSale() {
  const settings = useSiteSettings();
  const PORTAL_URL = settings?.portalUrl || process.env.REACT_APP_PORTAL_URL;

  return (
    <section id="materials" className="py-20 bg-transparent border-t border-white/5">
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, order: 2 }}
            whileInView={{ opacity: 1, order: window.innerWidth > 1024 ? 1 : 2 }}
            viewport={{ once: true }}
            className="relative overflow-visible"
          >
            <LazyImage
              src={hardwareImg}
              alt="Hardware and Materials"
              className="w-full h-[450px] object-cover rounded-2xl shadow-2xl transition-all duration-500"
            />
            <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-2xl hidden md:block z-20">
              <p className="text-black font-black text-2xl lg:text-3xl leading-none">Premium</p>
              <p className="text-gray-500 uppercase text-[10px] tracking-widest mt-1">Hardware & Accessories</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <p className="text-secondary font-bold tracking-[.3em] uppercase text-xs">
              Raw Materials & Supply
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
              Engineered <br /> Components
            </h2>
            <p className="text-white/70 text-lg leading-relaxed">
              Beyond windows and doors, we are a leading supplier of architectural grade aluminium alloys and high-performance hardware. From rollers and handles to custom-cut profiles, we provide materials that define longevity.
            </p>

            <ul className="space-y-4 pt-4">
              {[
                "Architectural Grade Aluminium (6063, T5/T6)",
                "Industrial Sections & Custom Profiles",
                "High-Precision Hardware (Rollers, Multi-point Locks)",
                "Surface Finishes: Powder Coated & Anodized"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-4 text-white/90 font-medium">
                  <div className="w-2 h-2 rounded-full bg-white" />
                  {item}
                </li>
              ))}
            </ul>

            {settings?.showBrowseMaterials && (
              <div className="pt-8">
                <a
                  href={PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-4 px-10 py-4 bg-white text-black font-bold rounded-full hover:bg-gray-200 transition-all group lg:scale-100"
                >
                  BROWSE MATERIALS
                  <span className="group-hover:translate-x-2 transition-transform">→</span>
                </a>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
